import { Link } from "react-router-dom";
import { useLang } from "@/lib/i18n.jsx";
import { Button } from "@/components/ui/button";
import { ArrowUpRight } from "lucide-react";

/**
 * Banner social + affiliati — logo, slogan, profili e porta al programma.
 *
 * Sta in un punto solo (`/` in fondo, e dove serve) invece di essere copiato
 * pagina per pagina: lo slogan e i profili sono la voce della marca, e due
 * copie divergono alla prima modifica. Il testo segue la lingua di sistema
 * come tutto il resto dell'app, quindi in EN non compare mezza frase italiana.
 *
 * I profili sono i nostri: se cambia un handle si cambia qui, non in tre
 * pagine. I marchi sono SVG inline monocromi (currentColor), come in
 * `BadgeShareButtons`, per non aggiungere una dipendenza.
 */
const SOCIAL = [
  {
    key: "instagram",
    label: "Instagram",
    href: "https://www.instagram.com/hostaly.it",
    path: (
      <>
        <rect x="3" y="3" width="18" height="18" rx="5" ry="5" fill="none" stroke="currentColor" strokeWidth="1.8" />
        <circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" strokeWidth="1.8" />
        <circle cx="17.5" cy="6.5" r="1.2" fill="currentColor" />
      </>
    ),
  },
  {
    key: "facebook",
    label: "Facebook",
    href: "https://www.facebook.com/hostaly.it",
    path: (
      <path
        d="M13.5 22v-8h2.7l.4-3.1h-3.1V8.9c0-.9.25-1.5 1.55-1.5h1.66V4.6c-.3-.05-1.3-.13-2.45-.13-2.42 0-4.06 1.47-4.06 4.18v2.33H7.5V14h2.7v8h3.3z"
        fill="currentColor"
      />
    ),
  },
  {
    key: "tiktok",
    label: "TikTok",
    href: "https://www.tiktok.com/@hostaly.it",
    path: (
      <path
        d="M14 3h2.5c.15 1.5 1.05 2.85 2.85 3.35v2.5c-1.1 0-2.15-.3-3.05-.85V15c0 3.05-2.45 5.5-5.5 5.5S5.3 18.05 5.3 15c0-3.05 2.45-5.5 5.5-5.5.35 0 .7.05 1.05.1v2.5a3 3 0 1 0 2.15 2.9V3z"
        fill="currentColor"
      />
    ),
  },
];

function BrandMark({ kind }) {
  const s = SOCIAL.find((x) => x.key === kind);
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">{s?.path}</svg>
  );
}

export default function SocialAffiliatesBanner({ className = "" }) {
  const { lang } = useLang();
  const IT = lang === "it";

  return (
    <section
      className={`border border-border rounded-2xl bg-card p-6 sm:p-8 ${className}`}
      data-testid="social-affiliates-banner"
    >
      <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
        {/* Logo + slogan — la voce della marca, in una riga sola */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3">
            <span
              className="w-10 h-10 shrink-0 rounded-md bg-primary text-primary-foreground grid place-items-center font-display text-2xl"
              aria-hidden="true"
            >
              H
            </span>
            <div className="min-w-0">
              <div className="font-display text-2xl tracking-tight leading-none" data-testid="social-banner-brand">Hostaly</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mt-1">
                {IT ? "Ospitalità italiana, nel mondo." : "Italian hospitality, worldwide."}
              </div>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mt-4 max-w-xl">
            {IT
              ? "Seguici per storie di strutture, mappe del tesoro e novità della piattaforma. E se hai un pubblico tuo, porti viaggiatori su Hostaly: loro hanno lo sconto, tu la commissione."
              : "Follow us for property stories, treasure hunts and platform news. And if you have an audience of your own, bring travellers to Hostaly: they get the discount, you get the commission."}
          </p>
        </div>

        {/* Social + porta agli affiliati */}
        <div className="flex flex-col items-start md:items-end gap-4 shrink-0">
          <div className="flex items-center gap-2" data-testid="social-banner-links">
            {SOCIAL.map((s) => (
              <a
                key={s.key}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                title={s.label}
                data-testid={`social-banner-${s.key}`}
                className="grid place-items-center w-10 h-10 rounded-full border border-border text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors"
              >
                <BrandMark kind={s.key} />
              </a>
            ))}
          </div>
          <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 lift" data-testid="social-banner-affiliate-cta">
            <Link to="/affiliati">
              {IT ? "Diventa affiliato" : "Become an affiliate"} <ArrowUpRight className="w-4 h-4 ml-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
